// src/store/modules/ivr.js
import IVRService from '@/services/IVRService'

const DTMF_KEYS = ['1','2','3','4','5','6','7','8','9','0','*','#']

function toLoopNumber(v) {
  const n = parseInt(String(v ?? '0').trim(), 10)
  if (Number.isNaN(n) || n < 0) return 0
  return n
}

function normalizeIvrRow(row) {
  if (!row) return null
  return {
    IVRID: String(row.IVRID ?? row.ivr_id ?? '').trim(),
    CustomerID: String(row.CustomerID ?? row.customer_id ?? '').trim(),
    IVRName: String(row.IVRName ?? row.ivr_name ?? row.name ?? '').trim(),
    GreetingID: row.GreetingID ?? row.greeting_id ?? null,
    Loop: toLoopNumber(row.Loop ?? row.loop ?? 0),
    LastUpdate: row.LastUpdate ?? row.updatedAt ?? null
  }
}

function normalizeMenuRow(row) {
  if (!row) return null
  return {
    IVRItemID: String(row.IVRItemID ?? row.ivr_item_id ?? '').trim(),
    IVRID: String(row.IVRID ?? row.ivr_id ?? '').trim(),
    CustomerID: String(row.CustomerID ?? row.customer_id ?? '').trim(),
    Option: String(row.Option ?? row.option ?? '').trim(),
    Action: String(row.Action ?? row.action ?? '').trim(),
    Destination: row.Destination ?? row.destination ?? null
  }
}

function normalizeRecRow(row) {
  if (!row) return null
  return {
    RecordingID: Number(row.RecordingID ?? row.recording_id ?? row.id ?? 0),
    CustomerID: String(row.CustomerID ?? row.customer_id ?? '').trim(),
    FileName: String(row.FileName ?? row.file_name ?? '').trim(),
    RecName: String(row.RecName ?? row.rec_name ?? '').trim()
  }
}

function menuRowsToMap(rows = []) {
  const map = {}
  DTMF_KEYS.forEach(k => {
    map[k] = { action: '', destination: '', itemId: '' }
  })

  rows.forEach(r => {
    const opt = String(r.Option ?? '').trim()
    if (!opt) return
    map[opt] = {
      action: r.Action || '',
      destination: r.Destination ?? '',
      itemId: r.IVRItemID || ''
    }
  })

  return map
}

function menuMapToRows(ivrId, menuMap = {}) {
  const rows = []
  Object.keys(menuMap || {}).forEach(opt => {
    const rule = menuMap[opt]
    if (!rule || !rule.action) return
    rows.push({
      IVRItemID: rule.itemId || null,
      IVRID: ivrId,
      Option: String(opt),
      Action: String(rule.action),
      Destination: rule.destination ?? null
    })
  })
  return rows
}

const state = () => ({
  ivrs: [],
  selectedIvrId: null,
  selectedIvr: null,

  menuRows: [],
  menuMap: menuRowsToMap([]),

  recordings: [],

  // ✅ Added ring_group bucket
  rollover: {
    internal_phone: [],
    voicemail: [],
    auto_attendant: [],
    call_queue: [],
    ring_group: [],
    call_forwarding: []
  },

  loading: {
    ivrs: false,
    ivr: false,
    menu: false,
    recordings: false,
    rollover: false,
    saving: false
  },

  error: null
})

const getters = {
  ivrs: s => s.ivrs,
  selectedIvrId: s => s.selectedIvrId,
  selectedIvr: s => s.selectedIvr,
  menuRows: s => s.menuRows,
  menuMap: s => s.menuMap,
  recordings: s => s.recordings,
  rollover: s => s.rollover,
  isLoading: s => Object.values(s.loading).some(Boolean),
  error: s => s.error,

  destinationGroups: (s) => {
    const mk = (key, label, arr) => ({
      key,
      label,
      options: (arr || []).map(o => ({
        // NOTE: preserve your existing behavior: use name first, then endpoint_id
        value: String(o.name ?? o.endpoint_id ?? o.value ?? ''),
        label: String(o.label ?? o.description ?? o.name ?? '')
      }))
    })

    return [
      mk('internal_phone', 'Internal Phones', s.rollover.internal_phone),
      mk('call_queue', 'Call Queues', s.rollover.call_queue),
      mk('ring_group', 'Ring Groups', s.rollover.ring_group), // ✅ NEW
      mk('voicemail', 'Voicemail', s.rollover.voicemail),
      mk('auto_attendant', 'Auto Attendants', s.rollover.auto_attendant),
      mk('call_forwarding', 'External Call Forward', s.rollover.call_forwarding)
    ]
  }
}

const mutations = {
  setError(s, err) {
    s.error = err ? String(err?.message || err) : null
  },

  setLoading(s, { key, val }) {
    s.loading[key] = !!val
  },

  setIvrs(s, rows) {
    s.ivrs = Array.isArray(rows) ? rows : []
  },

  setSelectedIvrId(s, id) {
    s.selectedIvrId = id
  },

  setSelectedIvr(s, row) {
    s.selectedIvr = row
  },

  setMenuRows(s, rows) {
    s.menuRows = Array.isArray(rows) ? rows : []
    s.menuMap = menuRowsToMap(s.menuRows)
  },

  setMenuMap(s, map) {
    s.menuMap = menuRowsToMap(menuMapToRows('__tmp__', map).map(r => ({
      Option: r.Option,
      Action: r.Action,
      Destination: r.Destination,
      IVRItemID: r.IVRItemID
    })))
  },

  setRecordings(s, rows) {
    s.recordings = Array.isArray(rows) ? rows : []
  },

  setRollover(s, grouped) {
    // ✅ include ring_group (safe default [])
    s.rollover = {
      internal_phone: grouped?.internal_phone || [],
      voicemail: grouped?.voicemail || [],
      auto_attendant: grouped?.auto_attendant || [],
      call_queue: grouped?.call_queue || [],
      ring_group: grouped?.ring_group || [],
      call_forwarding: grouped?.call_forwarding || []
    }
  },

  upsertIvrInList(s, row) {
    const id = row?.IVRID
    if (!id) return
    const idx = s.ivrs.findIndex(x => x.IVRID === id)
    if (idx >= 0) s.ivrs.splice(idx, 1, row)
    else s.ivrs.unshift(row)
  },

  removeIvrFromList(s, ivrId) {
    s.ivrs = s.ivrs.filter(x => x.IVRID !== ivrId)
    if (s.selectedIvrId === ivrId) {
      s.selectedIvrId = null
      s.selectedIvr = null
      s.menuRows = []
      s.menuMap = menuRowsToMap([])
    }
  }
}

const actions = {
  async fetchIvrs({ commit }) {
    commit('setLoading', { key: 'ivrs', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.listIvrs()
      const list = IVRService.unwrapList(res).map(normalizeIvrRow).filter(Boolean)
      commit('setIvrs', list)
      return list
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'ivrs', val: false })
    }
  },

  async selectIvr({ commit, dispatch }, ivrId) {
    const id = String(ivrId ?? '').trim()
    if (!id) return null

    commit('setSelectedIvrId', id)

    await Promise.all([
      dispatch('fetchIvr', id),
      dispatch('fetchMenuByIvr', id),
      dispatch('fetchRecordings'),
      dispatch('fetchRolloverOptions')
    ])

    return id
  },

  async fetchIvr({ commit }, ivrId) {
    const id = String(ivrId ?? '').trim()
    if (!id) throw new Error('Missing IVRID')

    commit('setLoading', { key: 'ivr', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.getIvr(id)
      const row = normalizeIvrRow(res?.data?.data ?? res?.data ?? null)
      if (!row) throw new Error('IVR not found')
      commit('setSelectedIvr', row)
      commit('upsertIvrInList', row)
      return row
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'ivr', val: false })
    }
  },

  async fetchMenuByIvr({ commit }, ivrId) {
    const id = String(ivrId ?? '').trim()
    if (!id) throw new Error('Missing IVRID')

    commit('setLoading', { key: 'menu', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.listMenuByIvr(id)
      const rows = IVRService.unwrapList(res).map(normalizeMenuRow).filter(Boolean)
      commit('setMenuRows', rows)
      return rows
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'menu', val: false })
    }
  },

  async fetchRecordings({ commit }) {
    commit('setLoading', { key: 'recordings', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.listRecordings()
      const rows = IVRService.unwrapList(res).map(normalizeRecRow).filter(Boolean)
      commit('setRecordings', rows)
      return rows
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'recordings', val: false })
    }
  },

  async fetchRolloverOptions({ commit }) {
    commit('setLoading', { key: 'rollover', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.rolloverOptions()
      const grouped = res?.data?.data ?? res?.data ?? null
      commit('setRollover', grouped)
      return grouped
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'rollover', val: false })
    }
  },

  async createIvr({ commit, dispatch }, { IVRName, GreetingID = null, Loop = 0 } = {}) {
    const name = String(IVRName ?? '').trim()
    if (!name) throw new Error('Missing IVRName')

    commit('setLoading', { key: 'saving', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.createIvr({ IVRName: name, GreetingID, Loop: toLoopNumber(Loop) })
      const ivr = normalizeIvrRow(res?.data?.data?.ivr ?? res?.data?.data ?? res?.data ?? null)
      if (!ivr?.IVRID) throw new Error('Create did not return IVRID')
      commit('upsertIvrInList', ivr)
      await dispatch('selectIvr', ivr.IVRID)
      return ivr
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'saving', val: false })
    }
  },

  async updateIvr({ commit }, { IVRID, IVRName, GreetingID = null, Loop = 0 } = {}) {
    const id = String(IVRID ?? '').trim()
    const name = String(IVRName ?? '').trim()
    if (!id) throw new Error('Missing IVRID')
    if (!name) throw new Error('Missing IVRName')

    commit('setLoading', { key: 'saving', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.updateIvr(id, { IVRName: name, GreetingID, Loop: toLoopNumber(Loop) })
      const ivr = normalizeIvrRow(res?.data?.data?.ivr ?? res?.data?.data ?? res?.data ?? null)
      if (ivr?.IVRID) commit('upsertIvrInList', ivr)
      if (ivr?.IVRID === id) commit('setSelectedIvr', ivr)
      return ivr
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'saving', val: false })
    }
  },

  async deleteIvr({ commit }, ivrId) {
    const id = String(ivrId ?? '').trim()
    if (!id) throw new Error('Missing IVRID')

    commit('setLoading', { key: 'saving', val: true })
    commit('setError', null)

    try {
      await IVRService.destroyIvr(id)
      commit('removeIvrFromList', id)
      return true
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'saving', val: false })
    }
  },

  async saveMenuMap({ state, commit, dispatch }, { IVRID, menuMap } = {}) {
    const id = String(IVRID ?? state.selectedIvrId ?? '').trim()
    if (!id) throw new Error('Missing IVRID')

    const map = menuMap || state.menuMap || {}

    commit('setLoading', { key: 'saving', val: true })
    commit('setError', null)

    try {
      const existing = (state.menuRows || []).map(normalizeMenuRow).filter(Boolean)
      const existingByOption = {}
      existing.forEach(r => { existingByOption[String(r.Option)] = r })

      const desiredRows = menuMapToRows(id, map)

      const desiredOptions = new Set(desiredRows.map(r => String(r.Option)))
      const deletes = existing
        .filter(r => !desiredOptions.has(String(r.Option)))
        .map(r => r.IVRItemID)
        .filter(Boolean)

      const creates = []
      const updates = []

      desiredRows.forEach(r => {
        const opt = String(r.Option)
        const prev = existingByOption[opt]
        if (!prev) {
          creates.push(r)
          return
        }

        const changed =
          String(prev.Action || '') !== String(r.Action || '') ||
          String(prev.Destination ?? '') !== String(r.Destination ?? '')

        if (changed) {
          updates.push({
            id: prev.IVRItemID,
            payload: { IVRID: r.IVRID, Option: r.Option, Action: r.Action, Destination: r.Destination }
          })
        }
      })

      await Promise.all([
        ...deletes.map(itemId => IVRService.destroyMenuItem(itemId)),
        ...updates.map(u => IVRService.updateMenuItem(u.id, u.payload)),
        ...creates.map(c => IVRService.createMenuItem({ IVRID: c.IVRID, Option: c.Option, Action: c.Action, Destination: c.Destination }))
      ])

      await dispatch('fetchMenuByIvr', id)
      return true
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'saving', val: false })
    }
  },

  // Step 1: Start recording call
  async startRecording({ commit }, { name } = {}) {
    const n = String(name ?? '').trim()
    if (!n) throw new Error('Missing recording name')

    commit('setLoading', { key: 'saving', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.startRecording(n)
      return res?.data ?? res
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'saving', val: false })
    }
  },

  // Step 2: "I'm finished" -> insert DB row
  async finishRecording({ commit, dispatch }, { file_token, name } = {}) {
    const t = String(file_token ?? '').trim()
    const n = String(name ?? '').trim()
    if (!t) throw new Error('Missing file_token')
    if (!n) throw new Error('Missing recording name')

    commit('setLoading', { key: 'saving', val: true })
    commit('setError', null)

    try {
      const res = await IVRService.finishRecording({ file_token: t, name: n })
      await dispatch('fetchRecordings')
      return res?.data ?? res
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'saving', val: false })
    }
  },

  async deleteRecording({ commit, dispatch }, recordingId) {
    const id = Number(recordingId)
    if (!id) throw new Error('Missing RecordingID')

    commit('setLoading', { key: 'saving', val: true })
    commit('setError', null)

    try {
      await IVRService.destroyRecording(id)
      await dispatch('fetchRecordings')
      return true
    } catch (e) {
      commit('setError', e)
      throw e
    } finally {
      commit('setLoading', { key: 'saving', val: false })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
